import type { ProductData } from '@/types/aggregation/common';
import { getRichTextPlainText, hasRichTextContent } from '@/app/components/rich-text/rich-text.utils';
import type {
    AppMarketingSliceItem,
    AppMarketingSlicePrimary
} from '@/features/app-marketing/types/app-marketing-slice';
import {
    isAppMarketingDayOneCardRenderable,
    isAppMarketingQrCodeItem
} from '@/features/app-marketing/logic/app-marketing-item';
import { getAppMarketingDisclaimerBlocks } from '@/features/app-marketing/logic/get-app-marketing-disclaimer-blocks';
import { POKER_APPS_SLICE_TYPE, type PokerAppsSlicePrimary } from '@/types/product/poker-apps-slice';

export type AppMarketingPlatform = 'ios' | 'android' | 'mac' | 'windows';

export type AppMarketingAppLinks = Partial<Record<AppMarketingPlatform, string>>;

export const emptyAppMarketingAppLinks = (): AppMarketingAppLinks => ({
    ios: undefined,
    android: undefined,
    mac: undefined,
    windows: undefined
});

export const mapPokerAppsPrimaryToAppLinks = (primary: PokerAppsSlicePrimary): AppMarketingAppLinks => ({
    ios: primary.ios_link?.url,
    android: primary.android_link?.url,
    mac: primary.macos_link?.url,
    windows: primary.windows_link?.url
});

export const getAppMarketingAppLinksFromProduct = (productData: ProductData | undefined): AppMarketingAppLinks => {
    const slices = productData?.extra_product_content;
    if (!Array.isArray(slices)) {
        return emptyAppMarketingAppLinks();
    }

    const pokerAppsSlice = slices.find((slice) => slice.slice_type === POKER_APPS_SLICE_TYPE);
    if (!pokerAppsSlice?.primary) {
        return emptyAppMarketingAppLinks();
    }

    return mapPokerAppsPrimaryToAppLinks(pokerAppsSlice.primary as PokerAppsSlicePrimary);
};

export const getAppMarketingHeroDesktopBackground = (primary: AppMarketingSlicePrimary): string | undefined =>
    primary.banner_background_image?.url;

export const getAppMarketingHeroMobileBackground = (primary: AppMarketingSlicePrimary): string | undefined =>
    primary.banner_mobile_background_image?.url ?? primary.banner_background_image?.url;

export const hasAppMarketingAppLinks = (appLinks: AppMarketingAppLinks): boolean =>
    Object.values(appLinks).some(Boolean);

const hasAccessibleAppMarketingTileContent = (item: AppMarketingSliceItem): boolean => {
    if (!item.image?.url) {
        return false;
    }

    return Boolean(
        item.image?.alt?.trim() || getRichTextPlainText(item.tile_title) || hasRichTextContent(item.tile_description)
    );
};

const isMobileVisibleMarketingTile = (item: AppMarketingSliceItem): boolean =>
    !isAppMarketingQrCodeItem(item) && hasAccessibleAppMarketingTileContent(item);

const hasRenderableAppMarketingItems = (items: AppMarketingSliceItem[]): boolean => {
    const hasMobileVisibleTiles = items.some(isMobileVisibleMarketingTile);

    return items.some((item) => {
        if (isAppMarketingQrCodeItem(item)) {
            // QR cards are desktop-only; do not activate takeover when they are the sole tile content.
            return isAppMarketingDayOneCardRenderable(item) && hasMobileVisibleTiles;
        }

        return hasAccessibleAppMarketingTileContent(item);
    });
};

export const hasAppMarketingRenderableContent = (
    primary: AppMarketingSlicePrimary,
    items: AppMarketingSliceItem[],
    appLinks: AppMarketingAppLinks = emptyAppMarketingAppLinks()
): boolean => {
    const hasPageHeading = Boolean(getRichTextPlainText(primary.banner_title));

    if (!hasPageHeading) {
        return false;
    }

    const hasTiles = hasRenderableAppMarketingItems(items);
    const hasNonTileContent = Boolean(
        getAppMarketingHeroDesktopBackground(primary) ||
            getAppMarketingHeroMobileBackground(primary) ||
            primary.banner_small_image?.url ||
            getAppMarketingDisclaimerBlocks(primary.banner_description).length > 0 ||
            hasAppMarketingAppLinks(appLinks)
    );

    return Boolean(hasNonTileContent || hasTiles || hasPageHeading);
};
